if (!customElements.get('complete-the-look')) {
  customElements.define(
    'complete-the-look',
    class CompleteTheLook extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.addButton = this.querySelector('[data-complete-look-add]');
        this.buttonText = this.querySelector('[data-complete-look-button-text]');
        this.totalElement = this.querySelector('[data-complete-look-total]');
        this.errorElement = this.querySelector('[data-complete-look-error]');
        this.mainItem = this.querySelector('[data-main-product]');
        this.mainVariants = this.readJson('[data-complete-look-main-variants]');
        this.variantCache = new WeakMap();
        this.variantChangeUnsubscriber = null;
        this.loading = false;

        this.onChange = this.onChange.bind(this);
        this.onAdd = this.onAdd.bind(this);
        this.onMainVariantInputChange = this.onMainVariantInputChange.bind(this);
        this.onMainVariantChange = this.onMainVariantChange.bind(this);
      }

      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.addEventListener('change', this.onChange);
        this.addButton?.addEventListener('click', this.onAdd);

        this.mainVariantInput = document
          .getElementById(`product-form-${this.dataset.sectionId}`)
          ?.querySelector('input[name="id"]');
        this.mainVariantInput?.addEventListener('change', this.onMainVariantInputChange);

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onMainVariantChange
          );
        }

        this.querySelectorAll('[data-complete-look-item]').forEach((item) => {
          this.updateItemSelectedClass(item);
        });

        this.syncMainVariantFromInput();
        this.renderSummary();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.addButton?.removeEventListener('click', this.onAdd);
        this.mainVariantInput?.removeEventListener('change', this.onMainVariantInputChange);
        this.variantChangeUnsubscriber?.();
      }

      readJson(selector, root = this) {
        const script = root.querySelector(selector);
        if (!script) return [];

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Complete the Look: invalid variant JSON.', error);
          return [];
        }
      }

      getVariants(item) {
        if (item.hasAttribute('data-main-product')) return this.mainVariants;
        if (this.variantCache.has(item)) return this.variantCache.get(item);

        const variants = this.readJson('[data-complete-look-variants]', item);
        this.variantCache.set(item, variants);
        return variants;
      }

      onChange(event) {
        const option = event.target.closest('[data-complete-look-option]');
        if (option) {
          const item = option.closest('[data-complete-look-item]');
          if (item) this.updateComplementaryVariant(item);
          return;
        }

        const checkbox = event.target.closest('[data-complete-look-checkbox]');
        if (!checkbox) return;

        const item = checkbox.closest('[data-complete-look-item]');
        if (item) this.updateItemSelectedClass(item);
        this.hideError();
        this.renderSummary();
      }

      updateComplementaryVariant(item) {
        const variants = this.getVariants(item);
        const currentVariant = variants.find(
          (variant) => String(variant.id) === String(item.dataset.variantId)
        );
        const selectedOptions = Array.from(
          item.querySelectorAll('[data-complete-look-option]')
        ).map((select) => ({
          position: Number(select.dataset.optionPosition) - 1,
          value: select.value
        }));

        if (!selectedOptions.length || !variants.length) return;

        const desiredOptions = currentVariant
          ? [...currentVariant.options]
          : [...(variants[0]?.options || [])];

        selectedOptions.forEach((selectedOption) => {
          desiredOptions[selectedOption.position] = selectedOption.value;
        });

        const exactVariant = variants.find((variant) =>
          variant.options.every((value, index) => value === desiredOptions[index])
        );
        const matchingVariants = variants.filter((variant) =>
          selectedOptions.every(
            (selectedOption) =>
              variant.options[selectedOption.position] === selectedOption.value
          )
        );
        const selectedVariant =
          exactVariant || matchingVariants.find((variant) => variant.available) || matchingVariants[0];

        this.applyVariantToItem(item, selectedVariant);
        this.hideError();
        this.renderSummary();
      }

      applyVariantToItem(item, variant) {
        const checkbox = item.querySelector('[data-complete-look-checkbox]');
        const availability = item.querySelector('[data-complete-look-availability]');
        const variantTitle = item.querySelector('[data-complete-look-variant-title]');
        const price = item.querySelector('[data-complete-look-price]');

        if (!variant) {
          item.dataset.variantId = '';
          item.dataset.price = '0';
          item.dataset.available = 'false';
          item.classList.add('complete-look__item--unavailable');
          if (checkbox) {
            checkbox.checked = false;
            checkbox.disabled = true;
          }
          if (availability) availability.hidden = false;
          return;
        }

        item.dataset.variantId = variant.id;
        item.dataset.price = variant.price;
        item.dataset.available = String(Boolean(variant.available));
        item.classList.toggle('complete-look__item--unavailable', !variant.available);

        if (variantTitle) {
          variantTitle.textContent =
            variant.title === 'Default Title'
              ? this.dataset.defaultVariantText
              : variant.title;
        }
        if (price) price.innerHTML = this.formatMoney(variant.price);
        if (availability) availability.hidden = Boolean(variant.available);

        if (checkbox && !item.hasAttribute('data-main-product')) {
          checkbox.disabled = !variant.available;
          if (!variant.available) checkbox.checked = false;
        }

        const image = item.querySelector('.complete-look__image');
        const variantImage = variant.featured_image?.src;
        if (image && variantImage) {
          image.removeAttribute('srcset');
          image.src = variantImage;
        }

        this.updateItemSelectedClass(item);
      }

      onMainVariantInputChange() {
        this.syncMainVariantFromInput();
      }

      onMainVariantChange(event) {
        const data = event?.data;
        if (!data?.variant) return;
        if (String(data.sectionId) !== String(this.dataset.sectionId)) return;

        this.updateMainVariant(data.variant.id);
      }

      syncMainVariantFromInput() {
        const variantId = this.mainVariantInput?.value;
        if (variantId) this.updateMainVariant(variantId);
      }

      updateMainVariant(variantId) {
        if (!this.mainItem) return;

        const variant = this.mainVariants.find(
          (candidate) => String(candidate.id) === String(variantId)
        );
        if (!variant) return;

        this.applyVariantToItem(this.mainItem, variant);
        this.hideError();
        this.renderSummary();
      }

      updateItemSelectedClass(item) {
        if (item.hasAttribute('data-main-product')) {
          item.classList.remove('complete-look__item--not-selected');
          return;
        }

        const checkbox = item.querySelector('[data-complete-look-checkbox]');
        item.classList.toggle('complete-look__item--not-selected', !checkbox?.checked);
      }

      selectedItems() {
        const selected = [];

        this.querySelectorAll('[data-complete-look-item]').forEach((item) => {
          const isMainProduct = item.hasAttribute('data-main-product');
          const checkbox = item.querySelector('[data-complete-look-checkbox]');
          const isSelected = isMainProduct || Boolean(checkbox?.checked);

          if (!isSelected || item.dataset.available !== 'true') return;

          const id = Number(item.dataset.variantId);
          const price = Number(item.dataset.price);
          if (!id) return;

          selected.push({ id, quantity: 1, price: Number.isFinite(price) ? price : 0 });
        });

        return selected;
      }

      renderSummary() {
        const items = this.selectedItems();
        const total = items.reduce((sum, item) => sum + item.price, 0);
        const formattedTotal = this.formatMoney(total);
        const mainAvailable = this.mainItem?.dataset.available === 'true';

        if (this.totalElement) this.totalElement.innerHTML = formattedTotal;
        if (this.buttonText) {
          this.buttonText.textContent = (this.dataset.buttonLabel || 'Add all {count} to bag · {total}')
            .replaceAll('{count}', items.length)
            .replaceAll('{total}', this.textOnly(formattedTotal));
        }

        if (this.addButton) {
          this.addButton.disabled = this.loading || !mainAvailable || items.length === 0;
        }
      }

      async onAdd() {
        if (this.loading || this.addButton?.disabled) return;

        this.syncMainVariantFromInput();
        const selectedItems = this.selectedItems();
        if (!selectedItems.length) return;

        const uniqueItems = [];
        const seenVariantIds = new Set();
        selectedItems.forEach((item) => {
          if (seenVariantIds.has(item.id)) return;
          seenVariantIds.add(item.id);
          uniqueItems.push({ id: item.id, quantity: item.quantity });
        });

        this.setLoading(true);
        this.hideError();

        const body = { items: uniqueItems };
        const sectionIds = this.cart?.getSectionsToRender?.().map((section) => section.id) || [];
        this.cart?.setActiveElement?.(document.activeElement);
        let itemsAdded = false;

        try {
          const response = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify(body)
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || this.dataset.errorLabel);
          }

          itemsAdded = true;

          const root = window.Shopify?.routes?.root || '/';
          const sectionsUrl = new URL(window.location.href);
          sectionsUrl.searchParams.set('sections', sectionIds.join(','));

          const [cartResponse, sectionsResponse] = await Promise.all([
            fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' }),
            sectionIds.length
              ? fetch(sectionsUrl, { credentials: 'same-origin', cache: 'no-store' })
              : Promise.resolve(null)
          ]);

          if (!cartResponse.ok || (sectionsResponse && !sectionsResponse.ok)) {
            throw new Error(this.dataset.errorLabel || 'Could not refresh the cart.');
          }

          const cartData = await cartResponse.json();
          const sections = sectionsResponse ? await sectionsResponse.json() : {};
          if (sectionIds.some((id) => typeof sections[id] !== 'string')) {
            throw new Error(this.dataset.errorLabel || 'Could not refresh the cart.');
          }

          cartData.sections = sections;
          cartData.id = cartData.items.find(
            (item) => String(item.variant_id) === String(uniqueItems[0].id)
          )?.key || uniqueItems[0].id;

          if (this.cart?.renderContents) {
            this.cart.renderContents(cartData);
          } else {
            window.location.assign(window.routes?.cart_url || `${root}cart`);
            return;
          }

          if (
            typeof publish === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined' &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'complete-the-look',
              productVariantId: uniqueItems[0].id,
              cartData
            });
          }
        } catch (error) {
          console.error(error);
          if (itemsAdded) {
            window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
          } else {
            this.showError(error.message || this.dataset.errorLabel);
          }
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.addButton?.classList.toggle('loading', isLoading);
        this.addButton?.toggleAttribute('aria-disabled', isLoading);

        const spinner = this.addButton?.querySelector('.loading__spinner');
        spinner?.classList.toggle('hidden', !isLoading);
        this.renderSummary();
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message || this.dataset.errorLabel;
        this.errorElement.hidden = false;
      }

      hideError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }

      textOnly(html) {
        const holder = document.createElement('div');
        holder.innerHTML = html;
        return holder.textContent || holder.innerText || '';
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = Number(cents) || 0;
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) return (value / 100).toFixed(2);

        const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
          const number = Number(amount);
          if (!Number.isFinite(number)) return '0';

          const parts = (number / 100).toFixed(precision).split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
          return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
        };

        let money;
        switch (match[1]) {
          case 'amount_no_decimals':
            money = formatWithDelimiters(value, 0);
            break;
          case 'amount_with_comma_separator':
            money = formatWithDelimiters(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            money = formatWithDelimiters(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            money = formatWithDelimiters(value, 2, "'", '.');
            break;
          default:
            money = formatWithDelimiters(value, 2);
        }

        return format.replace(match[0], money);
      }
    }
  );
}